// src/pages/analytics/DashboardsHubPage.tsx
// /analytics — dashboards hub. Browse, create, duplicate, delete. ListDashboards,
// CreateDashboard, DuplicateDashboard, DeleteDashboard.

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useI18n } from '@react/hooks';
import { Button, Badge, Alert } from '@react/components';
import { Select } from '@/components/Select';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { analyticsApi } from '@core/api';
import type { Dashboard, DashboardVisibility } from '@core/types/analytics';
import { useScope } from '@/hooks/useScope';
import { DashboardFormDialog, DASHBOARD_VISIBILITIES, errMessage, listParams } from '@/features/analytics';

export function DashboardsHubPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const scope = useScope();
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [visibility, setVisibility] = useState<DashboardVisibility | ''>('');
  const [createOpen, setCreateOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Dashboard | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    setLoading(true);
    setError(null);
    analyticsApi
      .ListDashboards(listParams({ limit: 50 }, visibility ? { visibility } : {}))
      .then((res) => setDashboards(res.items?.items ?? []))
      .catch((e: unknown) => setError(errMessage(e, t('analytics.dashboards.loadError'))))
      .finally(() => setLoading(false));
  };

  useEffect(load, [visibility]); // eslint-disable-line react-hooks/exhaustive-deps

  const duplicate = async (d: Dashboard) => {
    if (!d.entity?.id) return;
    try {
      const res = await analyticsApi.DuplicateDashboard(d.entity.id, {});
      const created = res.dashboard;
      if (created) setDashboards((prev) => [created, ...prev]);
    } catch (e) {
      setError(errMessage(e, t('analytics.dashboards.duplicateError')));
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete?.entity?.id) return;
    setDeleting(true);
    try {
      await analyticsApi.DeleteDashboard(confirmDelete.entity.id);
      setDashboards((prev) => prev.filter((d) => d.entity?.id !== confirmDelete.entity?.id));
    } catch (e) {
      setError(errMessage(e, t('analytics.dashboards.deleteError')));
    } finally {
      setDeleting(false);
      setConfirmDelete(null);
    }
  };

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('analytics.dashboards.title')}</h1>
        <div className="ml-auto flex items-center gap-2">
          <Link to="/analytics/queries" className="text-sm text-primary hover:underline">
            {t('analytics.nav.queries')}
          </Link>
          <Link to="/analytics/reports" className="text-sm text-primary hover:underline">
            {t('analytics.nav.reports')}
          </Link>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            {t('analytics.dashboards.create')}
          </Button>
        </div>
      </div>

      <Select aria-label={t('analytics.dashboards.visibility')} value={visibility} onChange={(e) => setVisibility(e.target.value as DashboardVisibility | '')} className="max-w-xs">
        <option value="">{t('analytics.dashboards.allVisibility')}</option>
        {DASHBOARD_VISIBILITIES.map((v) => (
          <option key={v} value={v}>
            {t(`analytics.visibility.${v}`)}
          </option>
        ))}
      </Select>

      {error && <Alert variant="error">{error}</Alert>}

      {loading && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" aria-busy="true">
          <div className="h-28 animate-pulse rounded-md bg-muted" />
          <div className="h-28 animate-pulse rounded-md bg-muted" />
          <div className="h-28 animate-pulse rounded-md bg-muted" />
        </div>
      )}

      {!loading && dashboards.length === 0 && !error && (
        <div className="rounded-md border border-dashed border-border p-10 text-center">
          <p className="text-sm text-muted-foreground">{t('analytics.dashboards.empty')}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t('analytics.dashboards.subtitle')}</p>
          <Button size="sm" className="mt-3" onClick={() => setCreateOpen(true)}>
            {t('analytics.dashboards.create')}
          </Button>
        </div>
      )}

      {!loading && dashboards.length > 0 && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {dashboards.map((d) => (
            <div key={d.entity?.id} className="flex flex-col rounded-md border border-border p-4 hover:border-primary">
              <button onClick={() => navigate(`/analytics/${d.entity?.id}`)} className="flex-1 text-left">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-foreground">{d.name}</span>
                  {d.is_default && <Badge variant="primary">{t('analytics.dashboards.default')}</Badge>}
                </div>
                {d.description && <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{d.description}</p>}
                <p className="mt-2 text-xs text-muted-foreground">{t(`analytics.visibility.${d.visibility}`)}</p>
              </button>
              <div className="mt-3 flex gap-2 border-t border-border pt-2">
                <Button size="sm" variant="ghost" onClick={() => duplicate(d)}>
                  {t('common.duplicate')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(d)}>
                  {t('common.delete')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <DashboardFormDialog open={createOpen} onClose={() => setCreateOpen(false)} scope={scope} onSaved={(d) => setDashboards((prev) => [d, ...prev])} />

      <ConfirmDialog
        open={!!confirmDelete}
        title={t('analytics.dashboards.deleteConfirmTitle')}
        description={t('analytics.dashboards.deleteConfirmBody', { name: confirmDelete?.name ?? '' })}
        destructive
        isConfirming={deleting}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
